/* eslint-disable sort-keys */
import React, { FormEvent, useEffect } from 'react';
import { observer, useLocalObservable } from 'mobx-react';
import isEmail from 'is-email';
import {
  Button,
  ButtonGroup,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import * as Sentry from '@sentry/react';
import { post } from '@lib/ApiClient';
import navigation from '@lib/history';
import store from '@lib/store';
import { USER_KEY, authToken } from '@lib/session';
import Dialog from '@component/Dialog';
import InfoDialog from '@component/InfoDialog';
import PhoneField from '@component/PhoneField';
import { Box, Loading } from '@atoms';

/**
 * Turns a failed auth request into something worth reading. 429 is new — the
 * API rate-limits the login endpoints now — and previously surfaced as a
 * generic "Error detected" box with no hint that waiting would fix it.
 */
function describeAuthFailure(e: any): string {
  const status = e?.response?.status;

  if (status === 429) {
    return 'Too many attempts. Please wait a few minutes and try again.';
  }

  if (status >= 500) {
    return 'The server is having trouble. Please try again shortly.';
  }

  if (status === undefined) {
    return 'Could not reach the server. Check your connection and try again.';
  }

  return 'Something went wrong signing in. Please try again.';
}

export const LoginForm = observer(function LoginForm() {
  const state = useLocalObservable(() => ({
    phone: null as string,
    email: null as string,
    didSendCode: false,
    didSendEmail: false,
    incorrectOTP: false,
    problem: null as string | null,
    verifyingOTP: false,
    signInLinkOTP: navigation.searchParams.get('otp'),
    mode: 'phone' as 'phone' | 'email',
    get maySubmit() {
      return (
        (state.mode === 'phone' && state.phone?.length === 11) ||
        isEmail(state.email)
      );
    },
    async submit(e: FormEvent<HTMLFormElement>) {
      e.preventDefault();

      if (state.mode === 'phone') {
        await state.requestOTP();
      } else {
        await state.requstLoginLink();
      }
    },
    async requestOTP() {
      try {
        await post('otp', { phone: state.phone });
        state.didSendCode = true;
        Sentry.addBreadcrumb({ message: 'requested OTP' });
      } catch (e) {
        state.problem = describeAuthFailure(e);
      }
    },
    async verifyOTP(otp: string) {
      try {
        state.verifyingOTP = true;
        state.problem = null;
        await post('sessions', { otp });
        await store.doPostLogin();
      } catch (e) {
        const { message } = (await e.response?.json().catch(() => ({}))) || {};

        if (message === 'invalid code') {
          state.incorrectOTP = true;
        } else {
          // Anything else — throttled, offline, server fault — belongs on this
          // form where the user can act on it, not in the global error toast,
          // which said only "Error detected" and left them stuck on a login
          // screen with no idea why their correct code was not working.
          state.problem = describeAuthFailure(e);
        }
      } finally {
        state.verifyingOTP = false;
      }
    },

    async requstLoginLink() {
      try {
        await post('login_email', { email: state.email });
        state.didSendEmail = true;
        Sentry.addBreadcrumb({ message: 'request login link' });
      } catch (e) {
        state.problem = describeAuthFailure(e);
      }
    },
  }));

  useEffect(() => {
    if (state.signInLinkOTP) {
      state.verifyOTP(state.signInLinkOTP);
    } else if (!store.user) {
      // Only tear down if this browser still holds something. Previously this
      // fired DELETE /api/session on every visit to the login screen, including
      // for a brand new visitor who had never signed in.
      if (authToken() || localStorage.getItem(USER_KEY)) {
        store.logOut({ navigateTo: null });
      }

      Sentry.addBreadcrumb({
        message: 'displayed login form',
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Dialog open={true}>
      <DialogTitle title="Sign In">Sign In</DialogTitle>
      {state.verifyingOTP ? (
        <DialogContent>
          <Box column>
            <Box>Authenticating...</Box>
            <Loading />
          </Box>
        </DialogContent>
      ) : state.didSendCode ? (
        <DialogContent>
          <Typography paragraph>
            We&apos;ve texted you a code. Enter it below.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            id="otp-code"
            disabled={state.verifyingOTP}
            label="Code"
            error={state.incorrectOTP}
            helperText={state.incorrectOTP ? 'Incorrect code' : undefined}
            // See PhoneField: the label needs an id to be an accessible name.
            inputProps={{ 'aria-label': 'Code', maxLength: 6, type: 'tel' }}
            onChange={({ target: { value: code } }) => {
              if (code?.length === 6) {
                state.verifyOTP(code);
              }
            }}
          />
          {state.problem ? (
            <Box fullWidth py={2}>
              <Alert severity="warning">{state.problem}</Alert>
            </Box>
          ) : null}
        </DialogContent>
      ) : state.didSendEmail ? (
        <DialogContent>
          <Box>
            We&apos;ve sent login instructions to your email address. You may close
            this tab or window.
          </Box>
        </DialogContent>
      ) : (
        <form
          style={{
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'column',
          }}
          onSubmit={state.submit}
        >
          <DialogContent
            style={{
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Box column>
              {state.incorrectOTP ? (
                <Box fullWidth py={2}>
                  <Alert severity="error">
                    Invalid or expired {state.signInLinkOTP ? 'link' : 'code'}
                  </Alert>
                </Box>
              ) : null}
              {state.problem ? (
                <Box fullWidth py={2}>
                  <Alert severity="warning">{state.problem}</Alert>
                </Box>
              ) : null}
              <Box fullWidth justifyContent="center">
                <ButtonGroup>
                  <Button
                    color="primary"
                    onClick={() => (state.mode = 'phone')}
                    variant={state.mode === 'phone' ? 'contained' : 'outlined'}
                  >
                    Phone
                  </Button>
                  <Button
                    color="primary"
                    onClick={() => (state.mode = 'email')}
                    variant={state.mode === 'email' ? 'contained' : 'outlined'}
                  >
                    Email
                  </Button>
                </ButtonGroup>
              </Box>
              {state.mode === 'phone' ? (
                <>
                  <Box
                    py={2}
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    Enter your phone number and we&apos;ll text you a one-time
                    password.
                    <InfoDialog title="One Time Password">
                      A secure way to sign in without having to remember a
                      password
                    </InfoDialog>
                  </Box>
                  <PhoneField
                    onChange={(phone: string) => (state.phone = phone)}
                    autoFocus
                  />
                </>
              ) : (
                <>
                  <Box
                    py={2}
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    Enter your email address and we&apos;ll send you a link to log
                    in.
                  </Box>
                  <TextField
                    fullWidth
                    label="Email"
                    onChange={e => (state.email = e.target.value)}
                    autoFocus
                  />
                </>
              )}
            </Box>
            <Box pt={2}>
              <Button type="submit" disabled={!state.maySubmit}>
                Send {state.mode === 'email' ? 'Link' : 'Code'}
              </Button>
            </Box>
          </DialogContent>
        </form>
      )}
    </Dialog>
  );
});

export default LoginForm;
